// Tracks browser notification permission state + offers enable() to request
// permission and subscribe. Registers the SW once on first mount, then
// auto-subscribes if permission was already granted.
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  pushSupported,
  registerServiceWorker,
  requestAndSubscribe,
  subscribePush,
} from "@/lib/push";

export type NotiState = "unsupported" | "default" | "granted" | "denied";

function readPermission(): NotiState {
  if (!pushSupported()) return "unsupported";
  return Notification.permission as NotiState;
}

export function useNotifications() {
  const [state, setState] = useState<NotiState>(readPermission);
  const navigate = useNavigate();

  useEffect(() => {
    if (!pushSupported()) return;
    void (async () => {
      await registerServiceWorker();
      const perm = readPermission();
      setState(perm);
      if (perm === "granted") {
        await subscribePush();
      }
    })();

    // Listen for SW postMessage({ type: 'navigate', url }) so clicking a
    // push notification while the app is already open navigates in-app.
    const onSwMessage = (e: MessageEvent) => {
      if (e.data?.type === "navigate" && typeof e.data.url === "string") {
        navigate(e.data.url);
      }
    };
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener("message", onSwMessage);
  }, [navigate]);

  const enable = useCallback(async () => {
    const perm = await requestAndSubscribe();
    setState(perm as NotiState);
    return perm;
  }, []);

  return { state, enable };
}
